/**
 * CycleBracket — Renders a single cycle marker in the Protocol panel.
 *
 * Combines with sibling CycleBracket components to partition sub-steps
 * by cycle coverage. Cycles are rendered with left vertical rule + label.
 *
 * Props:
 * - id: unique cycle ID (for React keys, stability)
 * - range: human-readable string, e.g., "2.1 → 2.2"
 * - count: iteration count, e.g., 3
 * - cycleIndex: order of this cycle in the group (0, 1, 2, ...)
 * - children: JSX sub-steps that belong to this cycle
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { getCycleColor } from '@/graph/cycleColors';

interface CycleBracketProps {
  id: string;
  range: string;
  count: number;
  cycleIndex: number;
  children: React.ReactNode;
}

export const CycleBracket: React.FC<CycleBracketProps> = ({
  id,
  range,
  count,
  cycleIndex,
  children,
}) => {
  const color = getCycleColor(cycleIndex);

  return (
    <Box
      key={id}
      sx={{
        display: 'flex',
        gap: 0,
        mb: 0,
      }}
    >
      {/* Left vertical rule */}
      <Box
        sx={{
          width: '3px',
          minHeight: '24px',
          bgcolor: color,
          flexShrink: 0,
        }}
      />

      {/* Sub-steps column with label */}
      <Box sx={{ pl: '20px', flex: 1 }}>
        {/* Cycle label badge */}
        <Typography
          variant="caption"
          sx={{
            color: color,
            fontWeight: 600,
            display: 'block',
            mb: 0.5,
          }}
        >
          ↻ {range} ({count} cycles)
        </Typography>

        {/* Sub-steps inside this cycle */}
        {children}
      </Box>
    </Box>
  );
};

export default CycleBracket;
